import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { ArrowLeftIcon } from '@radix-ui/react-icons'
import * as Separator from '@radix-ui/react-separator'
import { Input } from 'antd'
import { motion, useAnimationControls } from 'framer-motion'
import React, { createContext, useContext } from 'react'
import styled, { css } from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import type { DebouncedFunc } from 'utils/lodashTypes'

export const BackIcon = styled(ArrowLeftIcon)`
  color: ${({ theme }) => theme.forecast.icon1};
  cursor: pointer;
  height: 1.5rem;
  width: 1.5rem;
`

export const StyledBackContainer = styled.div`
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-items: center;
  gap: 0.25rem;
  border-radius: 0.25rem;
  border: 1px solid transparent;
  transition: border-color 0.2s ease-in-out;

  &:hover {
    border-color: ${({ theme }) => theme.forecast.background4};
  }
`

export const StyledSeparator = styled(Separator.Root)`
  background-color: ${({ theme }) => theme.forecast.background4};
  margin: 1.5rem;
  margin-right: 1rem;

  &[data-orientation='vertical'] {
    height: 60%;
    width: 1px;
  }
`

export const LeftContentContainer = styled.span`
  height: 100%;
  display: flex;
  align-items: center;
  justify-items: start;
  width: max-content;
`

export const StyledMenuItem = styled.div`
  display: flex;
  align-items: center;
`

export const StyledSearch = styled(Input)`
  background-color: ${({ theme }) => theme.forecast.background3} !important;
  color: ${({ theme }) => theme.forecast.text1} !important;
  max-width: 500px;
  flex-grow: 1;

  ::placeholder {
    color: ${({ theme }) => theme.forecast.text3} !important;
  }

  &:hover {
    border-color: ${({ theme }) => theme.forecast.background4} !important;
  }

  &:focus {
    border-color: ${({ theme }) => theme.forecast.background4} !important;
  }
`

export const MenuStyles = css`
  overflow: hidden;
  text-align: left;
  border-radius: 0.5rem;
  background-color: ${({ theme }) => theme.forecast.glassBackground};
  backdrop-filter: blur(32px);
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.1),
    0px 4px 8px 0px rgba(0, 0, 0, 0.1);
  -webkit-backdrop-filter: blur(32px);
`

export const FilteredSearchContainer = styled.div`
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export const StyledDropdownContent = styled(DropdownMenu.Content)`
  ${MenuStyles}
`

const StyledMotionDiv = styled(motion.div)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const DropdownSearchMenuContext = createContext<{
  closeMenu(): Promise<void>
  firstItemRef: React.MutableRefObject<HTMLDivElement | null>
  inputRef: React.MutableRefObject<HTMLInputElement | null>
  searchText: string
  debouncedResults?: DebouncedFunc<
    (e: React.ChangeEvent<HTMLInputElement>) => void
  >
}>({
  firstItemRef: { current: null },
  inputRef: { current: null },
  searchText: '',
  closeMenu: () => Promise.resolve(),

  debouncedResults: undefined,
})

export const StyledDropdownItem = styled(DropdownMenu.Item)`
  padding: 0.375rem 0.5rem;
  border-radius: 0.25rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  user-select: none;
  border: 1px solid transparent;
  width: 100%;
  background-color: ${({ theme }) => theme.forecast.defaultGhostButton};

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
  }

  &:active {
    color: ${({ theme }) => theme.forecast.activeText};
    background-color: ${({ theme }) => theme.forecast.activeGhostButton};
  }

  & [data-edit-link='true'] {
    display: none;
  }

  &:hover [data-edit-link='true'] {
    cursor: pointer;
    display: block;
  }
`

export function DropdownSearchMenuItem({
  isFirstItem,
  isSelected,
  children,
  onSelect = () => {
    // noop
  },
}: {
  isFirstItem?: boolean
  isSelected?: boolean
  children: React.ReactNode
  onSelect?: () => void
}) {
  const controls = useAnimationControls()
  const { closeMenu, firstItemRef } = useContext(DropdownSearchMenuContext)
  const { theme } = useGeckoTheme()

  // Set initial background color based on selection state
  React.useEffect(() => {
    if (isSelected) {
      controls.start({
        backgroundColor: theme.forecast.hoverGhostButton,
        transition: { duration: 0.1 },
      })
    } else {
      controls.start({
        backgroundColor: 'transparent',
        transition: { duration: 0.1 },
      })
    }
  }, [isSelected, controls, theme])

  return (
    <StyledDropdownItem
      ref={isFirstItem ? firstItemRef : undefined}
      onSelect={async (e) => {
        e.preventDefault()

        await controls.start({
          backgroundColor: theme.forecast.text4,
          color: theme.forecast.background1,
          transition: { duration: 0.04 },
        })
        await controls.start({
          backgroundColor: theme.forecast.background2,
          color: theme.forecast.text1,
          transition: { duration: 0.04 },
        })

        await closeMenu()
        onSelect()
      }}
      asChild>
      <StyledMotionDiv animate={controls}>{children}</StyledMotionDiv>
    </StyledDropdownItem>
  )
}
